import { useCallback, useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { useGlobalStore } from '@highlight/ipc/react'
import { ONBOARDING_COMPLETE_FLAG } from '@shared/onboarding/onboarding-flags'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useUser } from '@renderer/hooks/useUser'
import {
  useContextGenerationProgress,
  type ContextGenerationSnapshot,
} from '@renderer/hooks/use-context-generation-progress'
import { HOW_YOU_WORK_DETAIL_ROUTE } from '@renderer/windows/features/settings/tabs/how-you-work/routes'
import { useContextNotificationMockSource, type ContextNotificationSource } from './mock'
import { contextNotificationRecordsAtom, type ContextNotificationRecord } from './state'

export type ContextNotificationPhase = 'hidden' | 'progress' | 'minimized' | 'finished'

export type ContextNotification = ReturnType<typeof useContextNotification>

/** How long after onboarding a still-generating snapshot counts as the first hydration. */
const FIRST_HYDRATION_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * How long after onboarding generation gets to settle. First hydration takes a
 * few minutes and the deep-research lock lapses at 20, so a run still unsettled
 * at 30 failed somewhere. Without a cutoff it would pin the learning card, which
 * has no close button, to Home.
 */
const GENERATION_SETTLE_MS = 30 * 60 * 1000

function isGenerationComplete(snapshot: ContextGenerationSnapshot): boolean {
  const integrationsSettled = snapshot.integrations.every((i) => i.status === 'complete' || i.status === 'failed')
  return integrationsSettled && snapshot.liveContext.status === 'complete'
}

/**
 * Context that first finished before this onboarding run belongs to an account
 * that already existed; a fresh account has no completions, or only ones after it.
 */
function contextPredatesOnboarding(snapshot: ContextGenerationSnapshot, onboardedAt: number): boolean {
  const firstCompletions = [
    snapshot.liveContext.firstCompletedAt,
    ...snapshot.integrations.map((i) => i.firstCompletedAt),
  ]
  return firstCompletions.some((at) => at !== undefined && Date.parse(at) < onboardedAt)
}

function phaseOf(
  record: ContextNotificationRecord | null,
  snapshot: ContextGenerationSnapshot | null,
  pastSettleBy: boolean,
): ContextNotificationPhase {
  if (!record || !record.eligible || record.dismissed || !snapshot) return 'hidden'
  if (isGenerationComplete(snapshot)) return 'finished'
  // The ready card opens the live context document, so it needs one to show.
  if (pastSettleBy) return snapshot.liveContext.status === 'complete' ? 'finished' : 'hidden'
  return record.minimized ? 'minimized' : 'progress'
}

/** Whether `at` has passed, re-rendering when it does. */
function useIsPast(at: number | null): boolean {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    if (at === null) return
    // Timed from the clock, not `now`, which dates from mount when `at` arrives later (onboarding finishing).
    const id = setTimeout(() => setNow(Math.max(Date.now(), at)), Math.max(0, at - Date.now()))
    return () => clearTimeout(id)
  }, [at])
  return at !== null && now >= at
}

/**
 * Whether this account gets the post-onboarding notification: onboarding
 * finished recently and none of the context predates it. Generation may
 * already be done by the time the first snapshot lands (the ready card shows
 * straight away); an account that already had context never qualifies.
 */
function isFirstHydration(snapshot: ContextGenerationSnapshot, onboardedAt: number): boolean {
  const withinWindow = Date.now() - onboardedAt < FIRST_HYDRATION_WINDOW_MS
  return withinWindow && !contextPredatesOnboarding(snapshot, onboardedAt)
}

/** The real source: the user's stored record, decided from the first snapshot seen, plus the live snapshot. */
function useStoredSource(userId: string | undefined): ContextNotificationSource {
  const [onboardedAt] = useGlobalStore<number>('user', ONBOARDING_COMPLETE_FLAG, 0)
  const [records, setRecords] = useAtom(contextNotificationRecordsAtom)
  const { snapshot } = useContextGenerationProgress()
  const record = userId ? (records[userId] ?? null) : null

  useEffect(() => {
    if (!userId || !snapshot || onboardedAt <= 0 || record) return
    setRecords((current) => ({
      ...current,
      [userId]: { eligible: isFirstHydration(snapshot, onboardedAt), minimized: false, dismissed: false },
    }))
  }, [userId, snapshot, onboardedAt, record, setRecords])

  const setRecord = useCallback<ContextNotificationSource['setRecord']>(
    (update) =>
      setRecords((current) => {
        if (!userId || !current[userId]) return current
        return { ...current, [userId]: update(current[userId]) }
      }),
    [userId, setRecords],
  )

  return { record, setRecord, snapshot, settleBy: onboardedAt > 0 ? onboardedAt + GENERATION_SETTLE_MS : null }
}

/**
 * The one-time "Highlight is learning how you work" notification that follows
 * onboarding: a progress card while context generates, a pill once minimized,
 * and a persistent "see what Highlight learned" card when it's done.
 *
 * Reads one source — the real record and snapshot, or the developer-menu mock
 * standing in for them — so nothing below this line knows which it got.
 */
export function useContextNotification() {
  const userId = useUser()?.id
  const stored = useStoredSource(userId)
  const { record, setRecord, snapshot, settleBy } = useContextNotificationMockSource(userId) ?? stored
  const setSidepanelState = useSetSidepanelState()
  const pastSettleBy = useIsPast(settleBy)

  const phase = phaseOf(record, snapshot, pastSettleBy)
  const progress = snapshot ? snapshot.progressPercent / 100 : 0

  const minimize = useCallback(() => setRecord((r) => ({ ...r, minimized: true })), [setRecord])
  const expand = useCallback(() => setRecord((r) => ({ ...r, minimized: false })), [setRecord])
  const dismiss = useCallback(() => setRecord((r) => ({ ...r, dismissed: true })), [setRecord])

  const openHowYouWork = useCallback(() => {
    setSidepanelState('hidden')
    void WindowIpcRenderer.openSettingsWindow(HOW_YOU_WORK_DETAIL_ROUTE)
    dismiss()
  }, [setSidepanelState, dismiss])

  return { phase, progress, minimize, expand, dismiss, openHowYouWork }
}
